import { ContractState } from '@ail/contracts-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { defineContractStateNew } from '../../../../helpers/Utils'
import { useFilteredCustomers } from '../../../../hooks/useFilteredCustomers'
import { useSearchContractsQuery } from '../../../../redux/api/contractsApi'
import LicencePlateFlag from '../../LicencePlateFlag/LicencePlateFlag'
import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner'
import { useSettings } from '../../../../hooks/useSettings'

export function ContractSearchNew() {
  const { t } = useTranslation('common')
  const [displayQuery, setDisplayQuery] = useState('')
  const [resultsDisplay, setResultsDisplay] = useState(true)
  const resultsRef = useRef<HTMLDivElement>(null)
  const [expDays] = useSettings('expiringDaysFilter')
  const debouncedQuery = useDebounce(displayQuery, 1500)

  const contracts = useSearchContractsQuery(
    { searchTerm: debouncedQuery, activeOnly: false },
    { skip: !debouncedQuery || debouncedQuery.trim().length < 3 }
  )
  const { customersMap } = useFilteredCustomers()

  useEffect(() => {
    const handleOutsideToggle = (e: MouseEvent) => {
      if (resultsRef.current?.contains(e.target as Node)) {
        setResultsDisplay(true)
      } else {
        setResultsDisplay(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideToggle)
    return () => {
      document.removeEventListener('mousedown', handleOutsideToggle)
    }
  }, [])

  const handleQueryChange = useCallback((value: string) => {
    setDisplayQuery(value)
  }, [])

  const handleContractSelet = useCallback(() => {
    setDisplayQuery('')
    setResultsDisplay(false)
  }, [])

  const highlightText = (text: string | undefined, highlight: string) => {
    if (!text) return text
    if (!highlight) return text

    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'))

    return (
      <span>
        {parts.map((part, key) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span key={key} className="highlighted">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    )
  }

  const results = contracts.data || []
  const showNoResults =
    results.length === 0 &&
    !!debouncedQuery &&
    !!displayQuery &&
    displayQuery.length >= 3 &&
    debouncedQuery === displayQuery &&
    !contracts.isLoading
  //    const expiringDays = Number(FrontendSettings.get(FrontendSettingsKeys.expiringDaysFilter) ?? 90)
  //Needs proper expiring status display by using eexpiring days filters
  return (
    <div ref={resultsRef} className="input-area contract-search">
      <input
        autoFocus={true}
        className="transparent"
        onBlur={(e) => !e.relatedTarget && e.target.focus()}
        type="text"
        name={`uniqueName${new Date().getTime()}`}
        autoComplete="off"
        placeholder={t('FIND_CONTRACT_BY_KEYWORD')}
        onChange={(e) => handleQueryChange(e.target.value)}
        value={displayQuery}
      />
      <button title={t('CLEAR')} className="clr-btn" onClick={handleContractSelet}>
        <span className="icon-clear" />
      </button>
      {contracts.isLoading && <LoadingSpinner color="#a8a8a8" size={18} className="contract-search-spinner" />}
      <i className="icon-search" />

      <div className="search-res-wrap">
        {showNoResults && (
          <div className="search-res-list">
            <div className="contract-item">
              <span className="noResults">{t('NO_RESULTS_FOUND')}</span>
            </div>
          </div>
        )}

        {results.length > 0 && resultsDisplay && (
          <div className="search-res-list">
            {results.slice(0, 50).map((c, key) => (
              <div className="contract-item" key={key}>
                <NavLink
                  to={`/${c.state === ContractState.Initialized ? 'orderAndDelivery' : 'generalContracts'}/details/${
                    c.id
                  }`}
                  onClick={handleContractSelet}
                >
                  <span className="base-info">
                    <span>
                      {highlightText(c.reference, displayQuery)}
                      <span className="gallikerInfo">
                        {' '}
                        {highlightText(c.vehicle?.['GallikerReference'], displayQuery)}
                      </span>
                    </span>
                    <label
                      className={`statusArea ${
                        c.state === ContractState.Initialized && !!c.vehicle?.chassisNumber
                          ? 'settingup'
                          : defineContractStateNew({ contract: c, expDays: Number(expDays) })?.toLowerCase()
                      }`}
                    >
                      {t(
                        `CONTRACT_${
                          c.state === ContractState.Initialized && !!c.vehicle?.chassisNumber
                            ? 'SETTINGUP'
                            : defineContractStateNew({ contract: c, expDays: Number(expDays) })
                        }`.toUpperCase()
                      )}
                    </label>
                  </span>
                  <span className="car-info">
                    <span className="infoArea">
                      <span className="driver-info">
                        {highlightText(
                          !!c.vehicle?.driver?.displayName && !c.poolCar
                            ? c.vehicle.driver.displayName
                            : !c.poolCar && !c.vehicle?.driver?.displayName
                            ? t('NOT_ASSIGNED')
                            : c.poolCar && !!c.location
                            ? `${t('POOL_CAR')} | ${c.location}`
                            : c.poolCar && (!c.location || !!c.vehicle?.driver?.displayName)
                            ? `${t('POOL_CAR')}`
                            : '',
                          displayQuery
                        )}
                      </span>
                      <span className="vehicle-info" title={c.vehicle?.description}>
                        {c.vehicle?.description}
                      </span>
                      {c.owner && customersMap[c.owner.split('//')[0]] && (
                        <div className="company-info">{`${customersMap[c.owner.split('//')[0]]?.name}`}</div>
                      )}
                    </span>
                    {c.vehicle?.licensePlate?.licenseNumber && (
                      <LicencePlateFlag
                        licenceNumber={c.vehicle.licensePlate.licenseNumber}
                        countryCode={c.vehicle.licensePlate.country}
                        width="12"
                        height="14"
                      />
                    )}
                  </span>
                </NavLink>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
